import type { FC, MouseEvent } from "react";
import { hasWindow } from "@litres/utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Popover } from "@litres/ui-kit/popover";
import {
  useEventListener,
  useWindowDimensions,
} from "@litres-libs/global-hooks";
import type { ISearchResultItem } from "../types";
import { trackerSearchClickInput } from "../utils";
import { SearchResult } from "../SearchResult/SearchResult";
import { SearchForm } from "./SearchForm";

import styles from "./DesktopSuggestionsSearch.module.scss";

interface DesktopSuggestionsSearchProps {
  searchValue: string;
  searchResultItems: ISearchResultItem[];
  onSearchChange: (value: string) => void;
  onItemClick: (item: ISearchResultItem, index?: number) => void;
  onItemDeleteClick: (value: string) => void;
  onSearch: (q: string) => void;
  onClearClick?: () => void;
}

export const DesktopSuggestionsSearch: FC<DesktopSuggestionsSearchProps> = ({
  searchValue,
  searchResultItems,
  onSearchChange,
  onItemClick,
  onItemDeleteClick,
  onSearch,
}) => {
  const [isPopoverVisibility, setPopoverVisibility] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { width, height } = useWindowDimensions();

  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleOnClick = () => {
    setPopoverVisibility(true);
    trackerSearchClickInput(hasWindow ? window.location.href : "");
  };

  const handleSearchItemClick = (item: ISearchResultItem, index?: number) => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    setIsLoading(true);
    onItemClick(item, index);
  };

  const handleSearchFormSubmit = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setPopoverVisibility(false);

      if (searchValue === "" && searchInputRef.current) {
        searchInputRef.current.focus();
        searchInputRef.current.setSelectionRange(0, 0);
        return;
      }

      onSearch(searchValue);
    },
    [onSearch, searchValue]
  );

  const handleOnFocus = () => {
    setPopoverVisibility(true);
  };

  const handleHidePopover = (event: Event) => {
    if (
      searchContainerRef.current &&
      (event.target === searchContainerRef.current ||
        searchContainerRef.current.contains(event.target as Node))
    ) {
      return;
    }
    setPopoverVisibility(false);
  };

  useEventListener("click", handleHidePopover);

  // запрещаем прокрутку страницы при открытом окне
  useEffect(() => {
    document.body.style.overflow = isPopoverVisibility ? "hidden" : "";

    return (): void => {
      document.body.style.overflow = "";
    };
  }, [isPopoverVisibility]);

  useEffect(() => {
    setPopoverVisibility(false);
  }, [width, height]);

  return (
    <div
      className={cn(styles.searchAB, { [styles.mobileSearch]: false })}
      ref={searchContainerRef}
    >
      <Popover
        placement="bottom-start"
        content={
          <SearchResult
            items={searchResultItems}
            isDesktop
            isUserAnonymous
            onItemClick={handleSearchItemClick}
            onItemDeleteClick={onItemDeleteClick}
          />
        }
        trigger={["none"]}
        contentClassName={styles.popoverContent}
        disableArrow
        offset={[0, 0]}
        hasMask
        visible={isPopoverVisibility}
        onVisibleChange={setPopoverVisibility}
        customCloseTriggers={["lostIntersect", "focusLost", "esc"]}
        portal={false}
        visuallyHiddenDismiss
      >
        <SearchForm
          isLoading={isLoading}
          isSearchFocused={isPopoverVisibility}
          setPopoverVisibility={setPopoverVisibility}
          onFocus={handleOnFocus}
          onChange={onSearchChange}
          onClick={handleOnClick}
          onSubmit={handleSearchFormSubmit}
          inputRef={searchInputRef}
          value={searchValue}
          placeholder="Искать на Литрес"
        />
      </Popover>
    </div>
  );
};
